import React, { useEffect, useState } from "react";
import { Zap, Wrench, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const minCredits = (byId, ids) => {
  const vals = ids.map((i) => byId[i]).filter((v) => typeof v === "number");
  return vals.length ? Math.min(...vals) : null;
};

export const PricingCompare = ({ onCta }) => {
  const { t } = useI18n();
  const [packages, setPackages] = useState([]);
  const [byId, setById] = useState({});

  useEffect(() => {
    api.get("/catalog").then(({ data }) => {
      setPackages(data.packages || []);
      const m = {};
      (data.services || []).forEach((s) => { m[s.id] = s.credits; });
      setById(m);
    }).catch(() => {});
  }, []);

  const cr = (n) => (typeof n === "number" ? t("pc.fromCredits", { n }) : "—");
  const cols = [
    {
      icon: Zap,
      title: t("pc.autoTitle"),
      tag: t("pc.autoTag"),
      desc: t("pc.autoDesc"),
      rows: [
        ["Stage 1", cr(byId.stage1)],
        [t("pc.removal"), cr(minCredits(byId, ["fap_off", "egr_off", "dtc_off", "adblue_off", "o2_off"]))],
        [t("pc.availability"), t("pc.availVal")],
      ],
      accent: false,
    },
    {
      icon: Wrench,
      title: t("pc.indivTitle"),
      tag: t("pc.indivTag"),
      desc: t("pc.indivDesc"),
      rows: [
        [t("pc.stage1Custom"), cr(byId.stage1)],
        [t("pc.complex"), cr(minCredits(byId, ["immo_off", "tcu_stage1", "dsg_tuning", "zf_gearbox", "hybrid_reset", "airbag_crash", "airbag_eeprom"]))],
        [t("pc.follow"), t("pc.included")],
      ],
      accent: true,
    },
  ];

  return (
    <section id="pricing" className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="pricing-compare">
      <div className="text-center mb-12">
        <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">{t("pc.kicker")}</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">{t("pc.title")}</h2>
        <p className="mt-4 text-zinc-400 max-w-xl mx-auto text-sm">
          {t("pc.subtitle")}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
        {cols.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.title} className={`glass rounded-md p-8 relative overflow-hidden ${c.accent ? "ring-1 ring-aurora-cyan/40" : ""}`}>
              {c.accent && <div className="m-stripe-bar absolute top-0 left-0" />}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm bg-aurora-cyan/10">
                  <Icon className="w-5 h-5 text-aurora-cyan" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight leading-none">{c.title}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan">{c.tag}</span>
                </div>
              </div>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">{c.desc}</p>
              <div className="space-y-3">
                {c.rows.map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
                    <span className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-signal-success shrink-0" /> {k}
                    </span>
                    <span className="font-mono text-sm font-semibold text-aurora-cyan shrink-0">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {packages.length > 0 && (
        <div className="mt-10 glass rounded-md p-8 max-w-4xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <h3 className="font-display text-xl font-bold uppercase tracking-tight">{t("pc.packsTitle")}</h3>
            <span className="font-mono text-[11px] text-zinc-500 uppercase tracking-widest">{t("pc.packsSub")}</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((p) => (
              <div key={p.id} data-testid={`landing-pack-${p.id}`} className={`rounded-sm border p-5 ${p.popular ? "border-aurora-cyan/40 bg-aurora-cyan/5" : "border-white/10 bg-white/5"}`}>
                {p.popular && <div className="font-mono text-[9px] uppercase tracking-widest text-aurora-cyan mb-1">{t("pc.popular")}</div>}
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.label}</div>
                <div className="font-display text-2xl font-bold mt-1">
                  {Number(p.price).toFixed(2).replace(/\.00$/, "")}€
                  {p.discount_pct > 0 && (
                    <span className="font-mono text-xs text-zinc-600 line-through ml-2">{Number(p.base_price).toFixed(0)}€</span>
                  )}
                </div>
                <div className="font-mono text-xs text-aurora-cyan flex items-center gap-1 mt-1">
                  <Check className="w-3 h-3" /> {p.credits} {t("pc.creditsWord")}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button onClick={onCta} data-testid="pricing-cta-btn" className="px-8 py-4 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm">
              {t("pc.startNow")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
